import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function WhatsIncluded() {
  const tomos = [{
    num: '01',
    title: 'Bases de la Valoración Clínica',
    problema: 'No sabés por dónde empezar frente al paciente.',
    aprendes: 'Primera impresión, entrevista inicial, signos vitales, valoración cefalocaudal, dolor, escalas y red flags.',
    cierre: 'Llegás a la valoración con una estructura clara, ordenada y reproducible.'
  }, {
    num: '02',
    title: 'Procedimientos Fundamentales',
    problema: 'Conocés los procedimientos, pero no siempre sabés cómo ejecutarlos con seguridad real.',
    aprendes: 'Bioseguridad, higiene, movilización, vías de administración, inyecciones, venopunción, curaciones, sondas y oxigenoterapia.',
    cierre: 'Aprendé a hacer los procedimientos más frecuentes con técnica, criterio y seguridad.'
  }, {
    num: '03',
    title: 'Enfermería Médico-Quirúrgica',
    problema: 'El cuidado del paciente internado te resulta amplio, desordenado y difícil de integrar.',
    aprendes: 'Ingreso, balance hídrico, dolor, pre y postoperatorio, respiratorio, cardiovascular, nutrición, inmovilidad y alta.',
    cierre: 'Organizá el cuidado del paciente internado desde el ingreso hasta el egreso.'
  }, {
    num: '04',
    title: 'Urgencias y Situaciones Clínicas',
    problema: 'Sabés teoría, pero te cuesta reconocer cuándo una situación ya es una urgencia.',
    aprendes: 'Paciente inestable, disnea, dolor torácico, sepsis, glucemia, crisis hipertensiva, convulsiones, anafilaxia, caídas y algoritmos.',
    cierre: 'Aprendé a detectar deterioro clínico y actuar sin quedarte paralizado/a.'
  }, {
    num: '05',
    title: 'Farmacología Aplicada y Cálculos',
    problema: 'La medicación y los cálculos siguen siendo un área donde dudás demasiado.',
    aprendes: '10 correctos, fármacos frecuentes, cálculo de dosis, goteo, diluciones, insulina y medicación de alto riesgo.',
    cierre: 'Administrá con más seguridad y calculá con criterio sin depender del azar.'
  }, {
    num: '06',
    title: 'Razonamiento Clínico y Casos',
    problema: 'La teoría sola no te alcanza para resolver situaciones reales.',
    aprendes: 'Priorización, diagnósticos de enfermería, pase de guardia, registro clínico y 15 casos resueltos paso a paso.',
    cierre: 'Entrená la forma de pensar que necesitás para clínica, internado y hospital.'
  }];
  return <section className="py-12 md:py-20 lg:py-24 bg-clinical-base" data-id="element-534">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-535">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14" data-id="element-536">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-mid mb-4" data-id="element-537">
            La biblioteca
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl sm:text-4xl md:text-5xl font-black text-clinical-deep mb-3 md:mb-4 leading-[1.05] uppercase" data-id="element-538">
            Qué incluye el Mega Pack
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg sm:text-xl md:text-2xl text-clinical-mid mb-4" data-id="element-539">
            6 tomos.{' '}
            <span className="accent-italic" data-id="element-540">Un sistema clínico completo.</span>
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-base md:text-lg text-clinical-ink leading-relaxed" data-id="element-541">
            Cada tomo está pensado para resolver un{' '}
            <span className="accent-italic" data-id="element-542">problema clínico real</span>. No son
            apuntes sueltos: es una biblioteca organizada para acompañarte desde
            la teoría hasta la práctica hospitalaria.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-10 md:mb-12" data-id="element-543">
          {tomos.map((tomo, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.05
        }} className="relative bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 border border-clinical-line hover:border-clinical-mid/30 transition-all overflow-hidden flex flex-col h-full" data-id="element-544">
              <div className="absolute -top-4 -right-2 md:-top-6 md:-right-4 text-[90px] md:text-[130px] font-display font-black leading-none select-none pointer-events-none z-0 text-clinical-soft" data-id="element-545">
                {tomo.num}
              </div>

              <div className="relative z-10 flex-grow" data-id="element-546">
                <div className="inline-block px-3 py-1 rounded-full bg-clinical-soft text-clinical-deep text-xs font-bold mb-4 tracking-wide uppercase" data-id="element-547">
                  Tomo {tomo.num.replace('0', '')}
                </div>

                <h4 className="text-xl md:text-2xl font-bold text-clinical-deep mb-5 pr-12 leading-tight uppercase" data-id="element-548">
                  {tomo.title}
                </h4>

                <div className="space-y-4" data-id="element-549">
                  <div data-id="element-550">
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-clinical-ink/50 mb-1" data-id="element-551">
                      Problema
                    </span>
                    <p className="text-clinical-ink text-sm md:text-base leading-snug" data-id="element-552">
                      {tomo.problema}
                    </p>
                  </div>

                  <div data-id="element-553">
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-clinical-mid mb-1" data-id="element-554">
                      Aprendés
                    </span>
                    <p className="text-clinical-ink/80 text-sm md:text-base leading-snug" data-id="element-555">
                      {tomo.aprendes}
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative z-10 mt-5 md:mt-6 pt-5 md:pt-6 border-t border-clinical-line" data-id="element-556">
                <p className="text-clinical-deep text-base md:text-lg leading-snug accent-italic" data-id="element-557">
                  "{tomo.cierre}"
                </p>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="flex justify-center px-4" data-id="element-558">
          <a href="#pricing" className="group inline-flex items-center justify-center gap-2 bg-clinical-cta hover:bg-clinical-ctaHover text-white font-semibold text-sm md:text-base px-7 md:px-9 py-4 rounded-full transition-all transform hover:scale-[1.02] shadow-[0_8px_24px_rgba(15,98,254,0.3)] w-full max-w-[360px] sm:w-auto uppercase tracking-wide" data-id="element-559">
            Quiero el Mega Pack completo
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-560" />
          </a>
        </motion.div>
      </div>
    </section>;
}